import { useEffect, useState } from "react";

import { useUser } from "@ui/hooks/useUser";

import { useConfig } from "@jti/app-context/useConfig";
import { createEvoClient, getWithdrawCandidates } from "@jti/evo-client/v5";
import { getUser } from "@jti/rest-api";

type WithdrawCandidate = {
  orderId: string;
  itemsCount: number;
  date: string;
};

type UseWithdrawalCandidatesReturn = {
  withdrawalCandidates: WithdrawCandidate[];
  isWithdrawalCandidatesLoading: boolean;
};

const createClient = (evoDomain: string, recaptchaId: string, evoTenant: string) => {
  const user = getUser();

  return createEvoClient({
    baseUrl: evoDomain,
    recaptchaId,
    tenantToken: evoTenant,
    ciamToken: user.ciam_token,
    throwOnError: true,
  });
};

const fetchWithdrawalCandidates = async (
  evoDomain: string,
  recaptchaId: string,
  evoTenant: string
): Promise<WithdrawCandidate[]> => {
  const client = createClient(evoDomain, recaptchaId, evoTenant);

  const response = await getWithdrawCandidates({
    client,
    throwOnError: true,
  });

  return (
    response.data?.data?.map((candidate: { order_id: string; items_count: number; date: string }) => ({
      orderId: candidate.order_id,
      itemsCount: candidate.items_count,
      date: candidate.date,
    })) ?? []
  );
};

export const useWithdrawalCandidates = (): UseWithdrawalCandidatesReturn => {
  const { user, isUserLoggedIn } = useUser();
  const { EVO_DOMAIN: evoDomain, "X-RECAPTCHA-ID": recaptchaId, EVO_TENANT: evoTenant } = useConfig();
  const [withdrawalCandidates, setWithdrawalCandidates] = useState<WithdrawCandidate[]>([]);
  const [isWithdrawalCandidatesLoading, setIsWithdrawalCandidatesLoading] = useState(false);

  useEffect(() => {
    if (!isUserLoggedIn || !user?.ciam_token) {
      setWithdrawalCandidates([]);
      setIsWithdrawalCandidatesLoading(false);
      return;
    }

    const loadCandidates = async () => {
      setIsWithdrawalCandidatesLoading(true);

      try {
        const candidates = await fetchWithdrawalCandidates(evoDomain, recaptchaId, evoTenant);

        setWithdrawalCandidates(candidates);
      } catch {
        setWithdrawalCandidates([]);
      } finally {
        setIsWithdrawalCandidatesLoading(false);
      }
    };

    loadCandidates();
  }, [evoDomain, evoTenant, isUserLoggedIn, recaptchaId, user?.ciam_token]);

  return {
    withdrawalCandidates,
    isWithdrawalCandidatesLoading,
  };
};
